<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  issues: { type: Array, default: () => [] },
  isDirty: { type: Boolean, default: false },
});

const emit = defineEmits(['focusNode']);

const { t } = useI18n();
const [open, toggleOpen] = useToggle();

const hasErrors = computed(() =>
  props.issues.some(issue => issue.severity === 'error')
);

const pill = computed(() => {
  if (props.issues.length) {
    return {
      icon: 'i-lucide-triangle-alert',
      label: t('AGENT_BOTS.VISUAL_BUILDER.VALIDATION.ISSUES', {
        count: props.issues.length,
      }),
      tone: hasErrors.value
        ? 'bg-n-ruby-3 text-n-ruby-11'
        : 'bg-n-amber-3 text-n-amber-11',
    };
  }
  if (props.isDirty) {
    return {
      icon: 'i-lucide-circle-dot',
      label: t('AGENT_BOTS.VISUAL_BUILDER.VALIDATION.UNSAVED'),
      tone: 'bg-n-alpha-2 text-n-slate-11',
    };
  }
  return {
    icon: 'i-lucide-circle-check',
    label: t('AGENT_BOTS.VISUAL_BUILDER.VALIDATION.ALL_GOOD'),
    tone: 'bg-n-teal-3 text-n-teal-11',
  };
});

const onPick = issue => {
  toggleOpen(false);
  if (issue.nodeId != null) emit('focusNode', issue.nodeId);
};
</script>

<template>
  <div v-on-clickaway="() => toggleOpen(false)" class="relative">
    <button
      type="button"
      class="flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium rounded-lg"
      :class="pill.tone"
      :disabled="!issues.length"
      @click="toggleOpen()"
    >
      <Icon :icon="pill.icon" class="size-3.5" />
      {{ pill.label }}
    </button>
    <div
      v-if="open && issues.length"
      class="absolute z-50 flex flex-col gap-0.5 p-1.5 mt-1 border shadow-lg w-72 max-h-80 overflow-y-auto ltr:right-0 rtl:left-0 top-full rounded-xl border-n-weak bg-n-solid-1"
    >
      <button
        v-for="issue in issues"
        :key="issue.id"
        type="button"
        class="flex items-start gap-2 px-2 py-1.5 text-xs rounded-lg text-start hover:bg-n-alpha-2"
        @click="onPick(issue)"
      >
        <Icon
          :icon="
            issue.severity === 'error'
              ? 'i-lucide-circle-alert'
              : 'i-lucide-triangle-alert'
          "
          class="mt-px size-3.5 shrink-0"
          :class="
            issue.severity === 'error' ? 'text-n-ruby-11' : 'text-n-amber-11'
          "
        />
        <span class="text-n-slate-12">
          {{ t(`AGENT_BOTS.VISUAL_BUILDER.${issue.key}`, issue.count) }}
        </span>
      </button>
    </div>
  </div>
</template>
